import React from 'react';
import { BaseModal } from './BaseModal';
import { ImageZoom } from './ImageZoom';

interface GlobalPrivacyRightsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalPrivacyRightsModal({ isOpen, onClose }: GlobalPrivacyRightsModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Why Privacy Rights Webforms Matter"
      size="large"
    >
      <div className="space-y-6 text-gray-600 dark:text-gray-300">
        {/* Introduction */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <p className="mb-4">
            Privacy rights webforms help individuals exercise control over their personal data. These forms offer a direct, 
            user-friendly way for people to submit requests related to how their data is collected, used, or deleted—across 
            a range of privacy laws around the world.
          </p>
          <p className="mb-4">
            Whether it's access to personal information, requesting deletion, or managing consent preferences, these 
            webforms serve as a universal intake method to meet compliance across global privacy regulations. They play 
            a key role in your trust-building strategy by making data rights accessible and transparent to all users, 
            regardless of their location.
          </p>
        </div>

        {/* Section 1: Creation & Configuration */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">How We Implement Webforms</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">External Link Implementation</h4>
            <p className="mb-4">
              This is the simplest method. A user clicks a button and is taken to a dedicated webform hosted externally. 
              It's easy to implement, requires no design changes to your site, and ensures consistent formatting across 
              platforms.
            </p>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/OT%20GUY%20share%20link%20pic%20for%20global%20privacy%20rights.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9PVCBHVVkgc2hhcmUgbGluayBwaWMgZm9yIGdsb2JhbCBwcml2YWN5IHJpZ2h0cy5wbmciLCJpYXQiOjE3NDU0MjU1NDksImV4cCI6MjAyOTI0OTU0OX0.hYSu2lPIsZc1FA202A4vfJr_tc_3I7Y9MOQbh-MqzCw"
              alt="External Link Webform Example"
              className="w-full h-auto object-contain rounded-lg"
            />
          </div>
        </div>

        {/* Section 2: Example Steps */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Example: Submitting a Deletion Request</h3>
          <p className="mb-4">To show users what they can expect, here are three example steps from a deletion request:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Step 1: Fill out basic identifying information</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/OT%20GUY%20submission%20confirmation.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9PVCBHVVkgc3VibWlzc2lvbiBjb25maXJtYXRpb24ucG5nIiwiaWF0IjoxNzQ1NDI1NjMzLCJleHAiOjIwMjkyNDk2MzN9.nYemKm1kCBlMY3yv3PnzFnKR1m2Zu_UyKiiV3RIqOyc"
                alt="Step 1: Basic Information Form"
                className="w-full h-81 object-cover rounded-lg"
              />
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Step 2: Confirm your request via email</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/OT%20GUY%20confirmation%20Email.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9PVCBHVVkgY29uZmlybWF0aW9uIEVtYWlsLnBuZyIsImlhdCI6MTc0NTQyNTcyMSwiZXhwIjoyMDI5MjQ5NzIxfQ.qsvTO6OOW4q4F3jE_dT88UhaP83wtCdMg5dbQ--lNYw"
                alt="Step 2: Email Confirmation"
                className="w-full h-85 object-cover rounded-lg"
              />
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Step 3: Receive a confirmation of deletion</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/OT%20GUY%20Request%20confirmed.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9PVCBHVVkgUmVxdWVzdCBjb25maXJtZWQucG5nIiwiaWF0IjoxNzQ1NDI1NzQ2LCJleHAiOjIwMjkyNDk3NDZ9.SPgCOzUGN5YusaCT8HnCM3B9KChtn9E1k5PkuzThI8U"
                alt="Step 3: Deletion Confirmation"
                className="w-full h-80 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Section 3: What Happens Next */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">What Happens Next</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              Once a request is submitted, it enters OneTrust's Privacy Rights Automation dashboard (formerly DSAR). Here, 
              your team can:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6">
              <li>Track and manage all privacy rights requests</li>
              <li>Verify user identities</li>
              <li>Communicate securely with requesters</li>
              <li>Document all actions taken</li>
              <li>Ensure compliance with regulatory timelines</li>
            </ul>
          </div>
        </div>

        {/* Section 4: OneTrust Dashboard */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Admin View: OneTrust Privacy Rights Dashboard</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              Here's what your team sees when managing privacy rights requests in OneTrust:
            </p>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/Privacy%20Right%20Request%20in%20OT.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9Qcml2YWN5IFJpZ2h0IFJlcXVlc3QgaW4gT1QucG5nIiwiaWF0IjoxNzQ1OTQ0OTAxLCJleHAiOjIwMjk3Njg5MDF9.zc4p7JiRPGSzMPhlinLGcOTHMpHkdSjCiNEQONEbFNE"
              alt="Admin View: OneTrust Privacy Rights Dashboard"
              className="w-full h-auto object-contain rounded-lg"
            />
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              The dashboard provides a comprehensive view of all requests, their status, and required actions, helping your team 
              maintain compliance and provide timely responses to privacy rights requests.
            </p>
          </div>
        </div>

        {/* Section 5: Results Summary & Data Sharing */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Admin View: Results Summary & Data Sharing</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              The privacy team can access a detailed summary page for each request:
            </p>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/Privacy%20Right%20Request%20Structured%20Data.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9Qcml2YWN5IFJpZ2h0IFJlcXVlc3QgU3RydWN0dXJlZCBEYXRhLnBuZyIsImlhdCI6MTc0NjAyMjE3NCwiZXhwIjoyMDI5ODQ2MTc0fQ.nbB6oj6UGyaaAfWFlAqONp9s6QR88_2IFsK3hcF_Buo"
              alt="Admin View: Results Summary Page"
              className="w-full h-auto object-contain rounded-lg"
            />
          </div>
        </div>

        {/* Section 6: Email Notifications */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Subject View: Email Notifications</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              Data subjects receive automatic email notifications whenever there's activity on their request:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6">
              <li>Comments posted to the request</li>
              <li>Data shared by the privacy team</li>
              <li>Status updates</li>
              <li>Secure access links for verification and review</li>
            </ul>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/Privacy%20Right%20Email%20for%20Data%20Deletion%20Sent.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9Qcml2YWN5IFJpZ2h0IEVtYWlsIGZvciBEYXRhIERlbGV0aW9uIFNlbnQucG5nIiwiaWF0IjoxNzQ2MDIxOTA4LCJleHAiOjIwMjk4NDU5MDh9.2SU5FhtXk61BoqjcJvypDthwndy02d1-vz70Q2T5BzI"
              alt="Email Notification Example"
              className="w-full h-auto object-contain rounded-lg"
            />
          </div>
        </div>

        {/* Section 7: Request Portal Overview */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Subject View: Request Portal Overview</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              When data subjects access the portal, they see a comprehensive list of all their submitted requests:
            </p>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/Privacy%20Right%20Request%20List.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9Qcml2YWN5IFJpZ2h0IFJlcXVlc3QgTGlzdC5wbmciLCJpYXQiOjE3NDYwMjE5ODcsImV4cCI6MjAyOTg0NTk4N30.2A7QUmXx78iSsbnNv6RnaF29_6wq8cI_G6_ZJN53X8c"
              alt="Request Portal Overview"
              className="w-full h-auto object-contain rounded-lg"
            />
          </div>
        </div>

        {/* Section 8: Data Subject Access */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Subject View: Data Subject Access</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              When data is shared, data subjects can:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6">
              <li>Access their request details</li>
              <li>View all comments and communications</li>
              <li>Review and download shared data</li>
              <li>Track request status and progress</li>
            </ul>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PRIVACY%20RIGHTS/Privacy%20Right%20Request%20Details.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUklWQUNZIFJJR0hUUy9Qcml2YWN5IFJpZ2h0IFJlcXVlc3QgRGV0YWlscy5wbmciLCJpYXQiOjE3NDYwMjIwMTUsImV4cCI6MjAyOTg0NjAxNX0.gIyuqXX8FtpU-N5shndKtaPerRL5FIImxcJCozIQLiE"
              alt="Data Subject Access View"
              className="w-full h-auto object-contain rounded-lg"
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}